'use client'

import { isAxiosError } from 'axios'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { nextApiClient } from '@/platform/http/nextApiClient'
import { getStytchJWT } from '@/utilities/stytch/getStytchJWT'
import { isImpersonatedStytchPayload } from '@/utilities/shared/stytchSession'
import type { SyncPayloadUserInputType } from '@/types/next-api.generated'

export type SyncPayloadUserInput = SyncPayloadUserInputType

function getSyncAnalyticsContext(input: SyncPayloadUserInput) {
  return {
    hasPartnerId: Boolean(input.partnerId),
    scopeCount: Object.keys(input.scopes || {}).length,
    impersonated: isImpersonatedStytchPayload(getStytchJWT()),
  }
}

function getServerErrorMessage(data: unknown): string | undefined {
  if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
    return data.error
  }
  return undefined
}

/**
 * Sync a user to Payload CMS for admin access.
 * Returns true on success, false on non-2xx, and never throws.
 */
export async function syncUserToPayload(input: SyncPayloadUserInput): Promise<boolean> {
  try {
    await nextApiClient.post_payload_users_sync({
      email: input.email,
      name: input.name,
      orgSlug: input.orgSlug,
      partnerId: input.partnerId,
      scopes: input.scopes || {},
    })
    trackEvent('payload:user_sync_success', getSyncAnalyticsContext(input))
    return true
  } catch (error) {
    // Expected auth/validation failures (missing session, wrong email/org) are warn-level;
    // unexpected network/client errors stay error-level for dashboards.
    if (isAxiosError(error) && error.response) {
      trackEvent('payload:user_sync_fail', {
        ...getSyncAnalyticsContext(input),
        status: error.response.status,
        serverError: getServerErrorMessage(error.response.data),
      })
      console.warn(
        'Failed to sync user to Payload CMS:',
        error.response.status,
        error.response.statusText,
      )
      return false
    }

    trackEvent('payload:user_sync_fail', {
      ...getSyncAnalyticsContext(input),
      ...errorAnalyticsPayload(error),
    })
    console.error('Error syncing user to Payload CMS:', errorAnalyticsPayload(error))
    return false
  }
}

export const PayloadRepository = {
  syncUserToPayload,
}
